/** bump when the share card's template changes, so previews fetch it anew */
const CARD_REVISION = 1
/** the same for the performer card */
const PERFORMER_CARD_REVISION = 1

/**
 * The concert card's formats: `link`, what Facebook and most link previews
 * show (the page's `og:image`), and Instagram's feed post (4:5) and Story or
 * Reel (9:16), for whoever posts the concert there.
 */
export const SHARE_CARD_FORMATS = {
	link: { width: 1200, height: 630 },
	'4x5': { width: 1080, height: 1350 },
	'9x16': { width: 1080, height: 1920 },
} as const

export type ShareCardFormat = keyof typeof SHARE_CARD_FORMATS

/** The share card's size: what Facebook and most link previews show. */
export const SHARE_CARD_SIZE = SHARE_CARD_FORMATS.link

/** `?format=` on the card's URL: none is the link card; null if unknown. */
export function parseShareCardFormat(
	raw: string | null,
): ShareCardFormat | null {
	const format = raw ?? 'link'
	return Object.hasOwn(SHARE_CARD_FORMATS, format)
		? (format as ShareCardFormat)
		: null
}

type Versioned = {
	updatedAt: Date | string
	featuring?: Array<{ updatedAt?: Date | string }> | null
}

/**
 * Changes when anything on the card does (the concert, an artist's name, the
 * template), so a link preview that cached the old picture fetches anew.
 */
export function shareCardVersion(event: Versioned) {
	return `${CARD_REVISION}-${latestStamp([event.updatedAt, ...(event.featuring ?? []).map((p) => p.updatedAt)])}`
}

/**
 * "/events/abc/delingsbilete.jpg?v=1-mf3k2", or with "format=4x5&" before
 * the version for a tall card
 */
export function shareCardPath(
	event: Versioned & { id: string },
	format: ShareCardFormat = 'link',
) {
	const query = format === 'link' ? '' : `format=${format}&`
	return `/events/${event.id}/delingsbilete.jpg?${query}v=${shareCardVersion(event)}`
}

type PerformerVersioned = {
	updatedAt: Date | string
	events?: Array<{ date: Date | string; updatedAt?: Date | string }> | null
}

/** The concerts a performer's card lists: the ones still to come. */
export function upcomingConcerts<T extends { date: Date | string }>(
	events: ReadonlyArray<T> | null | undefined,
	now = new Date(),
) {
	return (events ?? []).filter(
		(event) => new Date(event.date).getTime() >= now.getTime(),
	)
}

/**
 * Changes when anything on the performer's card does: their name, bio or
 * photo, a concert's time, a concert passing (it leaves the card), the
 * template.
 */
export function performerShareCardVersion(
	performer: PerformerVersioned,
	now = new Date(),
) {
	const upcoming = upcomingConcerts(performer.events, now)
	const latest = latestStamp([
		performer.updatedAt,
		...upcoming.map((event) => event.updatedAt),
	])
	return `${PERFORMER_CARD_REVISION}-${latest}-${upcoming.length}`
}

/** "/performers/kari/delingsbilete.jpg?v=1-mf3k2-1" */
export function performerShareCardPath(
	performer: PerformerVersioned & { slug: string },
	now = new Date(),
) {
	return `/performers/${encodeURIComponent(performer.slug)}/delingsbilete.jpg?v=${performerShareCardVersion(performer, now)}`
}

/** The latest of the dates, in base 36. */
function latestStamp(dates: Array<Date | string | undefined>) {
	return Math.max(
		...dates.map((date) => (date ? new Date(date).getTime() : 0)),
	).toString(36)
}

/** At most `max` characters, cut at a word, with an ellipsis. */
export function clipText(text: string, max: number) {
	const trimmed = text.trim()
	if (trimmed.length <= max) return trimmed
	const cut = trimmed.slice(0, max - 1)
	const space = cut.lastIndexOf(' ')
	return `${(space > max / 2 ? cut.slice(0, space) : cut).replace(/[\s,;:–—-]+$/, '')}…`
}
